'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { Sheet, SheetActions, SheetBody, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTrigger } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { CommsOptionEnum } from '../../molecules/comms-preference-row/comms-preference-row.logic.ts';
import type { CommsOption } from '../../molecules/comms-preference-row/comms-preference-row.tsx';
import { CommsPreferenceDivider, CommsPreferenceRowI18n, CommsPreferenceSection } from '../../molecules/comms-preference-row/comms-preference-row.tsx';
import type { CommsPreferencesFormValues } from './comms-preferences-sheet.logic.ts';
import { preferenceChannels, useCommsPreferencesSheet } from './comms-preferences-sheet.logic.ts';
export interface CommsPreferencesSheetProps {
  contactId: string;
}
export const CommsPreferencesSheet: React.FC<CommsPreferencesSheetProps> = t0 => {
  const $ = _c(62);
  const {
    contactId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my-settings", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("my-settings:comms.channels.email");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = {
      id: CommsOptionEnum.Email,
      label: t2,
      value: CommsOptionEnum.Email
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("my-settings:comms.channels.text");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      id: CommsOptionEnum.Text,
      label: t4,
      value: CommsOptionEnum.Text
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = {
      [CommsOptionEnum.Email]: t3,
      [CommsOptionEnum.Text]: t5
    };
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const channelOptions = t6;
  let t7;
  if ($[12] !== channelOptions) {
    t7 = Object.entries(preferenceChannels).map(t8 => {
      const [preference, t9] = t8;
      const {
        channels,
        isDisabled
      } = t9;
      return {
        preference: preference as keyof typeof preferenceChannels,
        options: channels.map(channel => channelOptions[channel]),
        isDisabled
      };
    });
    $[12] = channelOptions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const preferenceRows = t7;
  let t8;
  if ($[14] !== contactId) {
    t8 = {
      contactId
    };
    $[14] = contactId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    formDefaultValues,
    isOpen,
    isSaving,
    supporterName,
    handleSave,
    handleOpenChange
  } = useCommsPreferencesSheet(t8);
  const formId = useId();
  let t9;
  if ($[16] !== supporterName || $[17] !== t) {
    t9 = supporterName ? t("my-settings:comms.sheetTitle", {
      name: supporterName
    }) : t("my-settings:comms.sheetTitleFallback");
    $[16] = supporterName;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const title = t9;
  let t10;
  if ($[19] !== t) {
    t10 = t("my-settings:contactCard.changePreferences");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <SheetTrigger><LinkStyle noUnderline={true} noPadding={true} size="sm" inline={true}><button type="button">{t10}</button></LinkStyle></SheetTrigger>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== title) {
    t12 = <SheetHeader>{title}</SheetHeader>;
    $[23] = title;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== handleSave) {
    t13 = values => handleSave(values as CommsPreferencesFormValues);
    $[25] = handleSave;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== formDefaultValues) {
    t14 = {
      defaultValues: formDefaultValues
    };
    $[27] = formDefaultValues;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== preferenceRows) {
    t15 = <CommsPreferenceSection>{preferenceRows.map(_temp)}</CommsPreferenceSection>;
    $[29] = preferenceRows;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== formId || $[32] !== t13 || $[33] !== t14 || $[34] !== t15 || $[35] !== title) {
    t16 = <SheetBody><Form id={formId} aria-label={title} onSubmit={t13} options={t14}>{t15}</Form></SheetBody>;
    $[31] = formId;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = title;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("core:actions.cancel");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17) {
    t18 = <SheetClose asChild={true}><ButtonStyle size="lg" purpose="neutral" variant="secondary" fullWidth={true}><button type="button">{t17}</button></ButtonStyle></SheetClose>;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t) {
    t19 = t("core:actions.save");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== formId || $[44] !== t19) {
    t20 = <button type="submit" form={formId}>{t19}</button>;
    $[43] = formId;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== isSaving || $[47] !== t20) {
    t21 = <ButtonStyle fullWidth={true} isBusy={isSaving} disabled={isSaving}>{t20}</ButtonStyle>;
    $[46] = isSaving;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t18 || $[50] !== t21) {
    t22 = <SheetFooter><SheetActions>{t18}{t21}</SheetActions></SheetFooter>;
    $[49] = t18;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t12 || $[53] !== t16 || $[54] !== t22 || $[55] !== title) {
    t23 = <SheetContent title={title}>{t12}{t16}{t22}</SheetContent>;
    $[52] = t12;
    $[53] = t16;
    $[54] = t22;
    $[55] = title;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== handleOpenChange || $[58] !== isOpen || $[59] !== t11 || $[60] !== t23) {
    t24 = <Sheet open={isOpen} onOpenChange={handleOpenChange}>{t11}{t23}</Sheet>;
    $[57] = handleOpenChange;
    $[58] = isOpen;
    $[59] = t11;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  return t24;
};
function _temp(t0, index) {
  const {
    preference: preference_0,
    options,
    isDisabled: isDisabled_0
  } = t0;
  return <Fragment key={preference_0}>{index > 0 && <CommsPreferenceDivider />}<CommsPreferenceRowI18n preference={preference_0} options={options} isDisabled={isDisabled_0} /></Fragment>;
}